import React from 'react';

/**
 * Pure logic for the Dispatcher Metrics / LC Metrics report pages: the metric
 * definitions (summarize / format / color band) and the matrix view model.
 *
 * Originally ported from components/DispatcherMetricsCard.tsx, a hand-rolled
 * heatmap the Home dashboard drew from the same endpoint. That card was deleted
 * on 2026-09-16 when Home dropped it, so this module is now the single
 * definition of these metrics — there is no longer a twin to keep in sync.
 *
 * Conventions carried over from the card:
 *   - Cell heatmap colors are emitted as Tailwind classes (for TanTable's
 *     meta.cellBgFn) instead of inline hex, via `bandClass`. The OTD Impact
 *     data bars + company roll-up cards keep their inline hex (they are cell
 *     *content*, not backgrounds) and still take isDark via ExtraColCtx.
 *   - Rank shading cutoffs are precomputed per column by the table component.
 */

// ---- Types -----------------------------------------------------------------

export interface MetricRow {
  date: string;            // YYYY-MM-DD
  dispatcher: string;      // AD display_name
  dispatcher_key: string;  // v1_username
  region: string;
  dispatches: number;
  total_drivers: number;
  working_drivers: number;
  working_pct: number | null;
  legs: number;
  drivers_worked: number;
  legs_per_driver: number | null;
}

// Which detail modal a metric opens (drives the modal's column set).
export type DetailKind = 'serviceFailures' | 'otd' | 'uncoded' | 'apptsFinalized' | 'servicePass';

// Delivery context shared by every detail modal row.
export interface DeliveryInfo {
  customer?: string;
  delivery_location?: string;
  delivery_appt?: string; // YYYY-MM-DD
  /**
   * The order's billto. Carried so the Uncoded drill's "add service failure" link can
   * hand it to RescheduleEditModal, which shows THD Reason only for a THD-family
   * bill-to. Null when the pro's billto is unset/unknown.
   */
  billto?: number | null;
}

// One controllable-reschedule row behind a Service Failures cell (drives the
// detail modal).
export interface ServiceFailureDetail extends DeliveryInfo {
  pro: number;
  date: string;       // YYYY-MM-DD (service failure date)
  reason: string;
  // What failed (reschedule.servicefailuretype); '' on legacy rows never typed.
  // Only 'Delivery Failure' rows appear as late deliveries in the OTD modal.
  type?: string;
  coded_by: string;
  resp_driver: string;
  comments: string;
}

// A late delivery inside an OTD cell — same shape as an SF detail (late
// deliveries ARE the controllable SFs typed "Delivery Failure"), plus the order
// dispatcher on the customer axis.
export interface LateDetail extends ServiceFailureDetail {
  dispatcher?: string;
}

// A planned delivery that fell off with no coded reason (Uncoded modal). No SF
// detail exists, so only the planned date + delivery context is shown.
export interface UncodedDetail extends DeliveryInfo {
  pro: number;
  date?: string; // YYYY-MM-DD (planned delivery date it fell off)
}

// One finalized appointment behind an Appts Finalized cell (drives the detail
// modal). `date` = the day the LC finalized it; `delivery_appt` = the appt set.
export interface ApptFinalizedDetail extends DeliveryInfo {
  pro: number;
  date?: string; // YYYY-MM-DD (finalize date)
}

export interface OrderRow {
  date: string;
  dispatcher: string;      // displayname
  dispatcher_key: string;  // username
  region: string;
  service_failures: number;
  service_failure_pros?: number[];
  service_failure_details?: ServiceFailureDetail[];
  uncoded_details?: UncodedDetail[];
  otd_denominator?: number;
  otd_numerator?: number;
  otd_pct?: number | null;
  late_pros?: number[];
  late_details?: LateDetail[];
  dispatchers?: string[];
  lcs?: string[];
  uncoded_planned?: number;
  uncoded_pros?: number[];
  // Customer-axis rows only: the customer's key status (cms.customers.keyaccount),
  // for the badge beside the name. '' / 'AVERAGE' draw nothing.
  key_status?: string;
  // Service Pass %: pros delivered on the day (delivery appt = D) vs those with
  // no controllable service failure at all (any date, any type; once per pro).
  pass_denominator?: number;   // delivered pros
  pass_numerator?: number;     // delivered pros with no C: SF
  pass_pct?: number | null;
  pass_fail_pros?: number[];   // the delivered pros that carry a C: SF
  pass_details?: LateDetail[]; // every C: SF on those pros (drives the modal)
}

// The key status behind a customer's rows (every row of an axis carries the same one).
export const keyStatusOf = (rows: AnyRow[]): string => {
  for (const r of rows) {
    const ks = (r as OrderRow).key_status;
    if (ks) return ks;
  }
  return '';
};

// Per-LC-per-day scheduling throughput (LC Metrics 'Scheduling' group). Same
// row keying as the other matrix tables (dispatcher / dispatcher_key hold the LC
// name / csr). Avg-time is carried as summed-seconds + count so the average can
// be re-weighted across date/week buckets.
export interface ScheduleRow {
  date: string;
  dispatcher: string;      // LC display name
  dispatcher_key: string;  // csr
  region: string;
  deliveries: number;      // pros the LC finalized that day
  delivery_pros?: number[];             // the finalized pros (for the drill-down)
  delivery_details?: ApptFinalizedDetail[]; // enriched detail rows for the modal
  lc_time_sum: number;     // Σ seconds on the LC for orders resolved that day
  lc_time_n: number;       // # orders resolved that day
}

// One LC's current scheduling backlog (point-in-time; not per-day). Drives the
// 'Pipeline (current)' snapshot panel.
export interface ScheduleSnapshotRow {
  dispatcher: string;      // LC display name
  dispatcher_key: string;  // csr
  pending_lc: number;
  hold: number;
  pending_customer: number;
  // Live avg "time on LC" clock (seconds) over this LC's current Pending LC
  // orders — now − send-to-LC, averaged. null when the LC has no pending orders.
  avg_pending_secs?: number | null;
  // Pending-LC "not finalized" backlog by due-date bucket (PENDING only), for the
  // finalize-matrix card. Deliveries bucket on the proposed appt date (orders with
  // no proposed delivery date are excluded); prepulls on the proposed prepull date
  // (unfinalized only). The two tables need not tie out.
  deliv_pastdue?: number;
  deliv_today?: number;
  deliv_plus1?: number;
  deliv_plus2?: number;
  deliv_future?: number;
  prepull_pastdue?: number;
  prepull_today?: number;
  prepull_plus1?: number;
  prepull_plus2?: number;
  prepull_future?: number;
}

/** Which finalize matrix a drilled cell belongs to. */
export type FinalizeKind = 'prepull' | 'delivery';

/** The due-date columns both finalize matrices are cut into. */
export type FinalizeBucket = 'pastdue' | 'today' | 'plus1' | 'plus2' | 'future';

/** Which cell of a finalize matrix is open. A null `bucket` is a Total across every
 *  bucket; a null `lc` is the table's Total row, across every LC. */
export interface FinalizeCell {
  kind: FinalizeKind;
  bucket: FinalizeBucket | null;
  lc: string | null;       // dispatcher_key (csr)
  lcName: string | null;   // display name, for the title
  label: string;           // the column heading clicked, for the title
  count: number;           // what the cell said, so the modal can show both
}

/** One order behind a "not finalized" cell. The proposed dates are what the LC
 *  entered; the cms_* pair is what CMS currently holds — the gap between them is
 *  what "not finalized" means. */
export interface FinalizeDetail {
  pro: number;
  lc: string;
  customer: string;
  movetype: string;
  container: string;
  schedule_status: string;
  proposed_prepull_date: string;
  proposed_appt_date: string;
  cms_prepull_date: string;
  cms_delivery_appt: string;
  delivery_location: string;
  available: string;
  comments: string;
}

export interface FinalizeDetailResponse {
  rows: FinalizeDetail[];
  truncated: boolean;
}

// One (dispatcher, period) driver headcount-flow cell, off the Fabric warehouse.
// `quarter` is 1-4 for a calendar quarter and 0 for the roll-up across the whole
// window. Both headcounts are measured on their boundary day, so they are never
// summed across periods — a driver on the roster all year is one driver.
//
// Each count carries the unit numbers behind it so every number on the table can
// open the drivers it is made of. `hire_units` / `term_units` are event lists and
// may repeat a unit that churned twice; the start / finish lists are distinct.
export interface TurnoverRow {
  date: string;            // the quarter's first day
  quarter: number;         // 1-4, or 0 for the window roll-up
  period_year: number;     // the quarter's year — a window can span two
  period_start: string;    // the period's span; the closing day is clamped to
  period_end: string;      // today for the quarter in progress
  dispatcher: string;      // AD display_name
  dispatcher_key: string;  // cms.dispatchers.username
  region: string;
  // Drivers no dispatcher on the page owns: none on record, or one who has left
  // / is not a GWI dispatcher. Without this line the table cannot reconcile to a
  // company figure.
  unassigned?: boolean;
  start_count: number;     // on the roster the period's first day
  hires: number;           // hire EVENTS in the period
  terms: number;           // termination EVENTS in the period
  finish_count: number;    // on the roster the period's last day
  // Roster snapshots are plain unit numbers; hires and terminations carry the
  // day the flag fired, which is what explains a driver first hired years
  // earlier appearing in this period — a rehire.
  start_units: string[];
  finish_units: string[];
  hire_units: TurnoverEvent[];
  term_units: TurnoverEvent[];
}

export interface TurnoverEvent {
  unit: string;
  date: string;  // 'YYYY-MM-DD'
}

// One driver behind a turnover number, keyed by unit number across the payload.
export interface TurnoverDriver {
  unit: string;
  name: string;
  preference: string;
  start_date: string;       // cms hire date, 'YYYY-MM-DD' or '' when unset
  // The day this unit's CURRENT spell on the roster began — the latest
  // transition into service, not the first. A unit number outlives its drivers,
  // so the earliest date usually belongs to someone else.
  in_service_since: string;
  term_date: string;
  status: string;
  /** The hire / termination that put this driver in the period being viewed. */
  event_date?: string;
  /** Set on a termination the same unit was hired back after, inside the same
   *  period. Taken from the events themselves, never inferred from `status`,
   *  which a rehire erases and which can be stale. */
  rehired?: boolean;
}

// One LC's V1-vs-360 usage totals for the adoption bar chart (not a matrix row).
export interface LcUtilizationRow {
  date: string;            // YYYY-MM-DD — one row per LC per day
  dispatcher: string;      // LC display name
  dispatcher_key: string;  // v1_username
  gwi360_count: number;    // saves that came through GWI 360 that day
  v1_count: number;        // saves that came through V1 that day
}

// One cms.history row an LC wrote through V1 (the adoption chart's drill-down).
export interface LcV1Save {
  recno: number;
  datetime: string;  // 'YYYY-MM-DD HH:MM:SS'
  table: string;     // cms table: ordersall, legsall, invoice, …
  pro: string;       // pronumber (history.keyvalue)
  sub: string;       // sub-key: leg number, invoice accessorial, line item, TT recno
  field: string;
  old: string;
  new: string;
  type: string;      // V1's save kind: single, global (Change All), dupe, edi, …
}
export interface LcV1SavesResponse {
  lc_key: string;
  lc_name: string;
  v1_username: string | null;
  total_rows: number;   // field changes in the range
  total_saves: number;  // distinct save-seconds in the range
  offset: number;
  limit: number;
  rows: LcV1Save[];
}

export type AnyRow = MetricRow | OrderRow | ScheduleRow | TurnoverRow;

export interface MetricsResponse {
  start_date: string;
  end_date: string;
  dates: string[];
  rows: MetricRow[];
  order_rows: OrderRow[];
  customer_rows?: OrderRow[];
  delivery_location_rows?: OrderRow[];
  lc_rows?: OrderRow[];  // order-side metrics re-keyed on the LC (ordersall.csr)
  schedule_rows?: ScheduleRow[];            // per-LC-per-day scheduling throughput
  schedule_snapshot?: ScheduleSnapshotRow[]; // current per-LC pipeline backlog
  lc_utilization?: LcUtilizationRow[];       // per-LC V1-vs-360 usage (adoption chart)
  lc_v1_saves?: LcV1SavesResponse;           // sections=v1_saves drill-down only
  turnover_rows?: TurnoverRow[];             // sections=turnover only
  turnover_drivers?: Record<string, TurnoverDriver>; // unit number -> driver detail
  turnover_window?: string;
}

// ---- Date helpers ----------------------------------------------------------

export const fmtDateKey = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// Parse 'YYYY-MM-DD' into a *local* Date (avoid UTC shift from new Date(str)).
export const parseLocal = (s: string): Date => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
};
export const isWeekend = (s: string): boolean => {
  const dow = parseLocal(s).getDay();
  return dow === 0 || dow === 6;
};
export const fmtMD = (s: string): string => {
  const [, m, d] = s.split('-').map(Number);
  return `${m}/${d}`;
};
export const fmtDow = (s: string): string =>
  ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][parseLocal(s).getDay()];

// Monday of the week containing a YYYY-MM-DD date.
export const weekStartKey = (s: string): string => {
  const d = parseLocal(s);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return fmtDateKey(d);
};

export const isDateKey = (s: string | undefined): s is string =>
  !!s && /^\d{4}-\d{2}-\d{2}$/.test(s);

// ---- Coloring --------------------------------------------------------------

// Semantic band -> Tailwind bg + text classes (light + dark). Values mirror the
// card's STATUS_COLORS tokens (green-100/800↔green-900/200, etc.).
export type Band = 'good' | 'warn' | 'bad';
export const bandClass = (kind: Band): string => {
  switch (kind) {
    case 'good':
      return 'bg-green-100 text-green-800 dark:bg-green-900/60 dark:text-green-200';
    case 'warn':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/60 dark:text-yellow-200';
    case 'bad':
      return 'bg-red-100 text-red-800 dark:bg-red-900/60 dark:text-red-200';
  }
};
export const NEUTRAL_TEXT = 'text-body';
export const MUTED_TEXT = 'text-gray-400 dark:text-gray-500';

// OTD threshold banding against the displayed (rounded) %: >=98 green, 96-97
// yellow, <96 red — the service expectation the card uses.
export const otdBand = (pct: number): Band =>
  pct >= 98 ? 'good' : pct >= 96 ? 'warn' : 'bad';

// Top-5 / bottom-5 rank band within a set of values. `best first` is decided by
// sortAsc (asc => lower is better). Ties share a band; green wins on overlap.
export const rankOf = (
  values: number[],
  val: number | null,
  sortAsc?: boolean,
): 'top' | 'bottom' | null => {
  if (val == null || values.length === 0) return null;
  const sorted = [...values].sort((a, b) => (sortAsc ? a - b : b - a));
  const greenCut = sorted[Math.min(5, sorted.length) - 1];
  const redCut = sorted[Math.max(sorted.length - 5, 0)];
  if (sortAsc ? val <= greenCut : val >= greenCut) return 'top';
  if (sortAsc ? val >= redCut : val <= redCut) return 'bottom';
  return null;
};

// ---- OTD Impact (volume-weighted) ------------------------------------------
// OTD Drag (pts) = Late Deliveries / Total Company Deliveries * 100. Bands:
// green <0.10, yellow 0.10-0.49, orange 0.50-0.99, red >=1.00.
type DragBand = 'green' | 'yellow' | 'orange' | 'red';
const dragBand = (pts: number): DragBand =>
  pts < 0.1 ? 'green' : pts < 0.5 ? 'yellow' : pts < 1 ? 'orange' : 'red';
const DRAG_COLORS: Record<DragBand, { text: { light: string; dark: string }; bar: string }> = {
  green:  { text: { light: '#15803d', dark: '#4ade80' }, bar: '#22c55e' },
  yellow: { text: { light: '#a16207', dark: '#facc15' }, bar: '#eab308' },
  orange: { text: { light: '#c2410c', dark: '#fb923c' }, bar: '#f97316' },
  red:    { text: { light: '#b91c1c', dark: '#f87171' }, bar: '#ef4444' },
};
export const fmtInt = (n: number): string => Math.round(n).toLocaleString('en-US');

// OTD roll-up helpers: numerator = on-time, denominator = total deliveries.
export const sumDenom = (rows: AnyRow[]): number =>
  rows.reduce((s, r) => s + ((r as OrderRow).otd_denominator || 0), 0);
export const sumOnTime = (rows: AnyRow[]): number =>
  rows.reduce((s, r) => s + ((r as OrderRow).otd_numerator || 0), 0);
// Service-failure detail rows + distinct pros behind a Service Failures cell.
export const serviceFailureDetails = (rows: AnyRow[]): ServiceFailureDetail[] =>
  rows.flatMap((r) => (r as OrderRow).service_failure_details || []);
export const serviceFailurePros = (rows: AnyRow[]): number[] =>
  Array.from(new Set(rows.flatMap((r) => (r as OrderRow).service_failure_pros || []))).sort((a, b) => a - b);
/** Bucket for a controllable failure nobody has typed yet. */
export const UNTYPED = 'Untyped';

/** The stored type of one failure row, with blanks bucketed as Untyped. */
const typeOf = (type: string | undefined): string => (type || '').trim() || UNTYPED;

/**
 * Order rows narrowed to a set of service-failure types (empty = untouched).
 *
 * The count and pro list are recomputed from the surviving detail rows rather
 * than kept from the server, so the cell, its breakdown and the detail modal
 * all agree with the filter instead of the cell claiming a total the modal
 * behind it cannot produce.
 */
/**
 * The metric as narrowed by an SF Type selection (empty = unchanged).
 *
 * Rows left with nothing drop out entirely: unfiltered, a dispatcher at 0 is
 * worth seeing (it means a clean day), but under a type filter a 0 only means
 * "none of the types you asked about", and listing every axis that way buries
 * the handful that actually have them.
 */
export const withFailureTypeFilter = (metric: MetricDef, types: string[]): MetricDef =>
  types.length === 0 ? metric : {
    ...metric,
    description: `${metric.description ?? ''} Counting only ${types.join(', ')}.`.trim(),
    hasData: (rows) => (metric.summarize(rows) ?? 0) > 0,
  };

export const filterRowsByFailureType = (rows: AnyRow[], types: string[]): AnyRow[] => {
  if (types.length === 0) return rows;
  const want = new Set(types);
  return rows.map((r) => {
    const o = r as OrderRow;
    const details = (o.service_failure_details ?? []).filter((d) => want.has(typeOf(d.type)));
    const pros = Array.from(new Set(details.map((d) => d.pro)));
    return { ...o, service_failure_details: details, service_failure_pros: pros, service_failures: pros.length };
  });
};

/** The one reschedule reason (cms.reschedulereason recno 22) the LC Metrics
 * page's All/CS Only toggle narrows to. */
export const CS_ISSUE_REASON = 'C: CUSTOMER SERVICE ISSUE';
export type ReasonScope = 'all' | 'cs_only';

/**
 * Order rows narrowed to just CS_ISSUE_REASON reschedules ('all' = unchanged).
 * Every controllable reschedule's `reason` already rides along in
 * service_failure_details / pass_details regardless of scope, so this is a
 * pure client-side recompute — no server round trip needed to flip the
 * toggle. Updates both Service Failures (service_failure_*) and Service %
 * (pass_fail_pros / pass_numerator / pass_pct / pass_details) from the
 * surviving detail rows, the same way filterRowsByFailureType does for SF type.
 */
export const filterRowsByReasonScope = (rows: AnyRow[], scope: ReasonScope): AnyRow[] => {
  if (scope !== 'cs_only') return rows;
  return rows.map((r) => {
    const o = r as OrderRow;
    const sfDetails = (o.service_failure_details ?? []).filter((d) => d.reason === CS_ISSUE_REASON);
    const sfPros = Array.from(new Set(sfDetails.map((d) => d.pro))).sort((a, b) => a - b);
    const passDetails = (o.pass_details ?? []).filter((d) => d.reason === CS_ISSUE_REASON);
    const failPros = Array.from(new Set(passDetails.map((d) => d.pro))).sort((a, b) => a - b);
    const denom = o.pass_denominator ?? 0;
    return {
      ...o,
      service_failure_details: sfDetails,
      service_failure_pros: sfPros,
      service_failures: sfPros.length,
      pass_details: passDetails,
      pass_fail_pros: failPros,
      pass_numerator: denom > 0 ? denom - failPros.length : o.pass_numerator,
      pass_pct: denom > 0 ? Math.round(((100 * (denom - failPros.length)) / denom) * 100) / 100 : o.pass_pct,
    };
  });
};

/**
 * Service-failure TYPE breakdown behind a cell, counted in DISTINCT PROS so it reconciles
 * with the number above it — the metric counts pros, not failure rows, and a pro with two
 * failures of one type must not count twice. A pro carrying two different types would
 * appear under both: possible, and rare enough to leave alone (one pro across a month of
 * test data), but it means the parts can exceed the whole by a hair rather than never.
 *
 * Untyped rows get their own entry rather than being dropped — the backfill typed the
 * history, so they are a thin tail (15 pros in 875 over a month of test data), but
 * omitting them would leave a breakdown that visibly fails to add up. They sort last
 * regardless of size: "not yet typed" is the absence of a failure kind, not one of them.
 */
export const serviceFailureTypeCounts = (rows: AnyRow[]): { type: string; pros: number }[] => {
  const byType = new Map<string, Set<number>>();
  for (const d of serviceFailureDetails(rows)) {
    const type = typeOf(d.type);
    if (!byType.has(type)) byType.set(type, new Set());
    byType.get(type)!.add(d.pro);
  }
  return Array.from(byType.entries())
    .map(([type, pros]) => ({ type, pros: pros.size }))
    // Most pros first, but Untyped always last however big it gets: it is the
    // absence of a type, not a kind of failure, so it never leads the list of
    // what actually went wrong.
    .sort((a, b) => {
      const au = a.type === UNTYPED ? 1 : 0;
      const bu = b.type === UNTYPED ? 1 : 0;
      return au - bu || b.pros - a.pros || a.type.localeCompare(b.type);
    });
};

/**
 * Type name without the trailing "Failure": every type ends in it, so in a breakdown
 * that is already labelled as service-failure types the word is pure repetition.
 * "Delivery Failure" -> "Delivery".
 */
export const failureTypeLabel = (type: string): string =>
  type.replace(/\s*Failure$/i, '').trim() || type;

/** The type breakdown on hover, for when Show details is off — one line per type,
 *  the same shape as the breakdown rows Show details opens up. */
export const serviceFailureTypeTooltip = (rows: AnyRow[]): string | null => {
  const counts = serviceFailureTypeCounts(rows);
  if (counts.length === 0) return null;
  return counts.map((c) => `${c.pros} ${failureTypeLabel(c.type)}`).join('\n');
};

// Late-delivery detail rows behind an OTD cell (one per late day, not deduped).
export const lateDetailRows = (rows: AnyRow[]): LateDetail[] =>
  rows.flatMap((r) => (r as OrderRow).late_details || []);
// Uncoded-planned detail rows + distinct pros behind an Uncoded cell.
export const uncodedDetailRows = (rows: AnyRow[]): UncodedDetail[] =>
  rows.flatMap((r) => (r as OrderRow).uncoded_details || []);
export const uncodedPros = (rows: AnyRow[]): number[] =>
  Array.from(new Set(rows.flatMap((r) => (r as OrderRow).uncoded_pros || []))).sort((a, b) => a - b);
// Finalized-appt detail rows + distinct pros behind an Appts Finalized cell.
export const apptFinalizedDetails = (rows: AnyRow[]): ApptFinalizedDetail[] =>
  rows.flatMap((r) => (r as ScheduleRow).delivery_details || []);
export const apptFinalizedPros = (rows: AnyRow[]): number[] =>
  Array.from(new Set(rows.flatMap((r) => (r as ScheduleRow).delivery_pros || []))).sort((a, b) => a - b);

// Late pros behind a set of OTD rows — one entry per late *day*, not deduped.
export const latePros = (rows: AnyRow[]): number[] =>
  rows.flatMap((r) => (r as OrderRow).late_pros || []).sort((a, b) => a - b);

// Service Pass % roll-ups: delivered pros vs delivered pros with no C: SF.
export const sumPassDenom = (rows: AnyRow[]): number =>
  rows.reduce((s, r) => s + ((r as OrderRow).pass_denominator || 0), 0);
export const sumPassNum = (rows: AnyRow[]): number =>
  rows.reduce((s, r) => s + ((r as OrderRow).pass_numerator || 0), 0);
// The failing pros behind a set of rows (distinct — a pro delivered on two
// days in the range fails on both but is one pro) + every C: SF on them.
export const passFailPros = (rows: AnyRow[]): number[] =>
  Array.from(new Set(rows.flatMap((r) => (r as OrderRow).pass_fail_pros || []))).sort((a, b) => a - b);
export const passFailDetails = (rows: AnyRow[]): LateDetail[] => {
  // One row per (pro, SF): a pro delivered on several days would otherwise
  // list its failures once per delivery day.
  const seen = new Set<string>();
  const out: LateDetail[] = [];
  for (const r of rows) {
    for (const d of (r as OrderRow).pass_details || []) {
      const k = `${d.pro}|${d.date}|${d.reason}|${d.type ?? ''}`;
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(d);
    }
  }
  return out;
};
// Reason-code breakdown for the late deliveries in a set of rows (hover text).
export const lateReasonTooltip = (rows: AnyRow[]): string | null => {
  const details = rows.flatMap((r) => (r as OrderRow).late_details || []);
  if (details.length === 0) return null;
  const counts = new Map<string, number>();
  for (const d of details) {
    const key = d.reason || '(no reason recorded)';
    counts.set(key, (counts.get(key) || 0) + 1);
  }
  const lines = Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([reason, n]) => (n > 1 ? `${reason} × ${n}` : reason));
  return [`${details.length} late, reason codes:`, ...lines].join('\n');
};

// Late-deliveries count for the OTD tables' Late column: links to the late pros
// (one occurrence per late day) with the reason codes on hover.
const renderLateCount = (rows: AnyRow[], ctx?: ExtraColCtx): React.ReactNode => {
  const late = sumDenom(rows) - sumOnTime(rows);
  const pros = latePros(rows);
  if (late <= 0 || pros.length === 0) return fmtInt(late);
  // Open the OTD detail modal when wired; otherwise fall back to the search link.
  if (ctx?.onOpenDetail) {
    return (
      <button
        type="button"
        onClick={() => ctx.onOpenDetail!(rows)}
        title={lateReasonTooltip(rows) ?? undefined}
        className="underline cursor-pointer bg-transparent border-0 p-0"
        style={{ color: 'inherit', font: 'inherit' }}
      >
        {fmtInt(late)}
      </button>
    );
  }
  return (
    <a
      href={`/searchresults?q=${pros.join(',')}&multi=true`}
      target="_blank"
      rel="noopener noreferrer"
      title={lateReasonTooltip(rows) ?? undefined}
      style={{ color: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}
    >
      {fmtInt(late)}
    </a>
  );
};

// Failed-pro count for the Service Pass tables' Failed column: links to the
// distinct failing pros (opens the detail modal when wired, else search).
const renderFailCount = (rows: AnyRow[], ctx?: ExtraColCtx): React.ReactNode => {
  const failed = sumPassDenom(rows) - sumPassNum(rows);
  const pros = passFailPros(rows);
  if (failed <= 0 || pros.length === 0) return fmtInt(failed);
  if (ctx?.onOpenDetail) {
    return (
      <button
        type="button"
        onClick={() => ctx.onOpenDetail!(rows)}
        className="underline cursor-pointer bg-transparent border-0 p-0"
        style={{ color: 'inherit', font: 'inherit' }}
      >
        {fmtInt(failed)}
      </button>
    );
  }
  return (
    <a
      href={`/searchresults?q=${pros.join(',')}&multi=true`}
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}
    >
      {fmtInt(failed)}
    </a>
  );
};

// Bold value + horizontal data bar, scaled to `max`. Shared by the OTD Drag
// (band-colored) and OTD MVP (green/red) impact columns.
const renderBarCell = (pts: number, max: number, text: string, bar: string, isDark: boolean): React.ReactNode => {
  const fill = max > 0 ? Math.min(100, (pts / max) * 100) : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
      <span style={{ fontWeight: 700, color: text, minWidth: 30, textAlign: 'right' }}>
        {pts.toFixed(2)}
      </span>
      <div style={{
        width: 54, height: 8, borderRadius: 4, overflow: 'hidden',
        background: isDark ? '#374151' : '#e5e7eb',
      }}>
        <div style={{ width: `${fill}%`, height: '100%', background: bar, borderRadius: 4 }} />
      </div>
    </div>
  );
};
const renderDragCell = (pts: number, ctx: ExtraColCtx): React.ReactNode => {
  const band = dragBand(pts);
  return renderBarCell(pts, ctx.maxDrag, DRAG_COLORS[band].text[ctx.isDark ? 'dark' : 'light'], DRAG_COLORS[band].bar, ctx.isDark);
};
const renderMvpCell = (loads: number, ctx: ExtraColCtx): React.ReactNode => {
  const r = Math.round(loads);
  const positive = r >= 0;
  const text = positive ? (ctx.isDark ? '#4ade80' : '#15803d') : (ctx.isDark ? '#f87171' : '#b91c1c');
  const bar = positive ? '#22c55e' : '#ef4444';
  const sign = r > 0 ? '+' : r < 0 ? '−' : '';
  const label = `${sign}${fmtInt(Math.abs(r))}`;
  const fill = ctx.maxMvp > 0 ? Math.min(100, (Math.abs(loads) / ctx.maxMvp) * 100) : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
      <span style={{ fontWeight: 700, color: text, minWidth: 34, textAlign: 'right' }}>{label}</span>
      <div style={{
        width: 54, height: 8, borderRadius: 4, overflow: 'hidden',
        background: ctx.isDark ? '#374151' : '#e5e7eb',
      }}>
        <div style={{ width: `${fill}%`, height: '100%', background: bar, borderRadius: 4 }} />
      </div>
    </div>
  );
};

// ---- Metric definitions ----------------------------------------------------

// Context shared by the OTD impact columns and company roll-up cards.
export interface ExtraColCtx {
  isDark: boolean;
  companyTotal: number;   // total company deliveries in range
  companyOnTime: number;
  maxDrag: number;        // largest drag among displayed rows (data-bar scale)
  maxMvp: number;         // largest |MVP| among displayed rows (data-bar scale)
  // When set, the Late column opens the OTD detail modal instead of linking to
  // search results.
  onOpenDetail?: (rows: AnyRow[]) => void;
}
// An extra summary column appended after the Total/Avg column (OTD only).
export interface ExtraCol {
  key: string;
  header: React.ReactNode;
  minWidth?: number;
  cell: (rows: AnyRow[], ctx: ExtraColCtx) => React.ReactNode;
  footer?: (rows: AnyRow[], ctx: ExtraColCtx) => React.ReactNode;
  // value a dispatcher row sorts on (companyAvg = company on-time fraction).
  sortValue?: (rows: AnyRow[], companyAvg: number) => number | null;
}

export type SummaryLabel = 'Total' | 'Avg';
export interface MetricDef {
  key: string;
  label: string;
  description?: string;
  summaryLabel: SummaryLabel;
  colorMode: 'pct' | 'relative' | 'none' | 'binary' | 'threshold';
  sortAsc?: boolean;
  noSummaryRank?: boolean;
  zeroNeutral?: boolean;
  emptyValue?: number | null;
  hasData?: (rows: AnyRow[]) => boolean;
  linkPros?: (rows: AnyRow[]) => number[];
  linkProsDedupe?: false;
  // When set, a cell with data opens a detail modal instead of linking straight
  // to search results (the modal itself offers the search link).
  detailKind?: DetailKind;
  cellTooltip?: (rows: AnyRow[]) => string | null;
  nameTooltip?: (rows: AnyRow[]) => string | null;
  detail?: (rows: AnyRow[]) => string | null;
  /**
   * Optional per-axis breakdown. With Show details on, the table renders one
   * sub-row per label under each dispatcher / customer / LC — `labels` names the
   * lines a row has (ordered), `value` fills one of them for one cell's rows.
   * Used for the service-failure type split, which never fit inside a cell.
   */
  breakdown?: {
    labels: (allRows: AnyRow[]) => string[];
    value: (cellRows: AnyRow[], label: string) => number | null;
    /** The same rows narrowed to one label, so a breakdown number opens the
     *  detail modal showing only that type's failures. */
    filter: (cellRows: AnyRow[], label: string) => AnyRow[];
  };
  summarize: (rows: AnyRow[]) => number | null;
  format: (v: number | null) => string;
  sortBy?: (rows: AnyRow[]) => number | null;
  extraCols?: ExtraCol[];
  footerPanel?: (allVisible: AnyRow[], ctx: ExtraColCtx) => React.ReactNode;
}

export const METRICS: MetricDef[] = [
  {
    key: 'dispatches',
    label: 'Dispatches',
    description: "Legs dispatched, attributed to each dispatcher and counted by the leg's appointment date.",
    summaryLabel: 'Total',
    colorMode: 'relative',
    emptyValue: 0,
    summarize: (rows) => rows.reduce((s, r) => s + (r as MetricRow).dispatches, 0),
    format: (v) => (v == null ? '—' : String(Math.round(v))),
  },
  {
    key: 'working_pct',
    label: 'Working %',
    description: 'Drivers who worked ÷ total drivers.',
    summaryLabel: 'Avg',
    colorMode: 'pct',
    summarize: (rows) => {
      const tot = rows.reduce((s, r) => s + (r as MetricRow).total_drivers, 0);
      const wrk = rows.reduce((s, r) => s + (r as MetricRow).working_drivers, 0);
      return tot > 0 ? (100 * wrk) / tot : null;
    },
    hasData: (rows) => rows.reduce((s, r) => s + ((r as MetricRow).total_drivers || 0), 0) > 0,
    detail: (rows) => {
      const tot = rows.reduce((s, r) => s + ((r as MetricRow).total_drivers || 0), 0);
      const wrk = rows.reduce((s, r) => s + ((r as MetricRow).working_drivers || 0), 0);
      return tot > 0 ? `${wrk}/${tot}` : null;
    },
    format: (v) => (v == null ? '—' : `${Math.round(v)}%`),
  },
  {
    key: 'legs_per_driver',
    label: 'Legs / Driver',
    description: 'Legs ÷ distinct drivers who worked.',
    summaryLabel: 'Avg',
    colorMode: 'relative',
    summarize: (rows) => {
      const legs = rows.reduce((s, r) => s + (r as MetricRow).legs, 0);
      const drv = rows.reduce((s, r) => s + (r as MetricRow).drivers_worked, 0);
      return drv > 0 ? legs / drv : null;
    },
    hasData: (rows) => rows.reduce((s, r) => s + ((r as MetricRow).legs || 0), 0) > 0,
    detail: (rows) => {
      const legs = rows.reduce((s, r) => s + ((r as MetricRow).legs || 0), 0);
      const drv = rows.reduce((s, r) => s + ((r as MetricRow).drivers_worked || 0), 0);
      return drv > 0 ? `${legs}/${drv}` : null;
    },
    format: (v) => (v == null ? '—' : v.toFixed(1)),
  },
];

// The OTD roll-up panel beside every OTD table: Company OTD, Total Late, and the
// two Impact explainers. `noun` names the axis the explainers talk about —
// 'dispatcher', 'customer' or 'LC' — so the same cards read correctly on each.
const otdFooterPanel = (noun: string): MetricDef['footerPanel'] => (_all, ctx) => {
  const total = ctx.companyTotal;
  const onTime = ctx.companyOnTime;
  const late = total - onTime;
  const otd = total > 0 ? (100 * onTime) / total : 0;
  const { isDark } = ctx;
  const cardBg = isDark ? '#111827' : '#f9fafb';
  const cardBorder = isDark ? '#374151' : '#e5e7eb';
  const label = isDark ? '#9ca3af' : '#6b7280';
  const green = isDark ? '#4ade80' : '#15803d';
  const red = isDark ? '#f87171' : '#b91c1c';
  const labelStyle: React.CSSProperties = {
    fontSize: 9, fontWeight: 600, color: label, textTransform: 'uppercase', letterSpacing: 0.4,
  };
  const card = (content: React.ReactNode): React.ReactNode => (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center', textAlign: 'center',
      background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 6, padding: '6px 12px',
    }}>
      {content}
    </div>
  );
  const explainCard = (title: string, accent: string, body: React.ReactNode): React.ReactNode => (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: 2, textAlign: 'left',
      background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 6, padding: '6px 10px',
    }}>
      <span style={{ ...labelStyle, color: accent }}>{title}</span>
      <span style={{ fontSize: 12, color: label, lineHeight: 1.4 }}>{body}</span>
    </div>
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0, width: 190 }}>
      {card(<>
        <span style={labelStyle}>Company OTD</span>
        <span style={{ fontSize: 26, fontWeight: 700, color: green, lineHeight: 1.05 }}>{Math.round(otd)}%</span>
        <span style={{ fontSize: 10, color: label }}>{fmtInt(onTime)} / {fmtInt(total)} loads</span>
      </>)}
      {card(<>
        <span style={labelStyle}>Total Late</span>
        <span style={{ fontSize: 20, fontWeight: 700, color: red, lineHeight: 1.05 }}>{fmtInt(late)}</span>
      </>)}
      {explainCard('Impact (−): Who hurts OTD', red, (
        <>Late loads as a share of all company loads. The <b>bigger</b> the number, the more this {noun} is dragging company OTD down. Sort by it to see where to focus.</>
      ))}
      {explainCard('Impact (+): Who helps OTD', green, (
        <>On-time loads <b>above</b> what an average {noun} would deliver at the same volume. Green = lifting the company average; red = below it. Rewards being on-time <i>and</i> high-volume.</>
      ))}
    </div>
  );
};

// Order-dispatcher metrics (different axis: cms.dispatchers.displayname).
export const ORDER_METRICS: MetricDef[] = [
  {
    key: 'service_failures',
    label: 'Service Failures',
    description: 'Distinct pros with a controllable (C:) service failure, counted on the recorded service failure date.',
    summaryLabel: 'Total',
    colorMode: 'binary',
    sortAsc: true,
    hasData: () => true,
    emptyValue: 0,
    linkPros: (rows) => rows.flatMap((r) => (r as OrderRow).service_failure_pros || []),
    cellTooltip: serviceFailureTypeTooltip,
    // Show details opens one row per failure type under each dispatcher / customer /
    // LC, rather than squeezing the breakdown into ~58px cells (where it clipped).
    breakdown: {
      labels: (rows) => serviceFailureTypeCounts(rows).map((c) => failureTypeLabel(c.type)),
      value: (rows, label) =>
        serviceFailureTypeCounts(rows).find((c) => failureTypeLabel(c.type) === label)?.pros ?? null,
      // Narrow each row to the one type, keeping the row shape the cell renderer
      // and the detail modal already read — so a type's number stays clickable
      // and opens only that type's failures.
      filter: (rows, label) => rows.map((r) => {
        const o = r as OrderRow;
        const details = (o.service_failure_details ?? []).filter(
          (d) => failureTypeLabel(typeOf(d.type)) === label,
        );
        const pros = Array.from(new Set(details.map((d) => d.pro)));
        return { ...o, service_failure_details: details, service_failure_pros: pros, service_failures: pros.length };
      }),
    },
    detailKind: 'serviceFailures',
    summarize: (rows) => rows.reduce((s, r) => s + ((r as OrderRow).service_failures || 0), 0),
    format: (v) => (v == null ? '0' : String(Math.round(v))),
  },
  // Sits next to Service Failures: it is the same failures read as a rate, so
  // the two are looked at together.
  {
    key: 'pass_pct',
    label: 'Service %',
    description: 'Delivered pros with no controllable (C:) service failure ÷ pros delivered that day (delivery appointment on the day). A pro fails if it carries any controllable service failure, of any type, on any date, before or after the delivery, and counts once no matter how many it has. 5 delivered, 1 with a controllable failure = 80%.',
    summaryLabel: 'Avg',
    colorMode: 'pct',
    linkPros: passFailPros,
    detailKind: 'servicePass',
    summarize: (rows) => {
      const den = sumPassDenom(rows);
      return den > 0 ? (100 * sumPassNum(rows)) / den : null;
    },
    hasData: (rows) => sumPassDenom(rows) > 0,
    detail: (rows) => {
      const den = sumPassDenom(rows);
      return den > 0 ? `${sumPassNum(rows)}/${den}` : null;
    },
    format: (v) => (v == null ? '—' : `${Math.round(v)}%`),
    extraCols: [
      {
        key: 'pass_delivered',
        header: (<><div>Delivered</div><div style={{ fontSize: 10, opacity: 0.7 }}>Clean / Total</div></>),
        minWidth: 110,
        cell: (rows) => `${fmtInt(sumPassNum(rows))} / ${fmtInt(sumPassDenom(rows))}`,
        footer: (rows) => `${fmtInt(sumPassNum(rows))} / ${fmtInt(sumPassDenom(rows))}`,
        sortValue: (rows) => sumPassDenom(rows),
      },
      {
        key: 'pass_failed',
        header: (<><div>Failed</div><div style={{ fontSize: 10, opacity: 0.7 }}>Pros</div></>),
        minWidth: 54,
        cell: (rows, ctx) => renderFailCount(rows, ctx),
        footer: (rows, ctx) => renderFailCount(rows, ctx),
        sortValue: (rows) => sumPassDenom(rows) - sumPassNum(rows),
      },
    ],
  },
  {
    key: 'uncoded_planned',
    label: 'Uncoded Planned Deliveries',
    description: 'Planned deliveries that fell off the date with no coded service failure reason.',
    summaryLabel: 'Total',
    colorMode: 'binary',
    sortAsc: true,
    noSummaryRank: true,
    zeroNeutral: true,
    emptyValue: 0,
    linkPros: (rows) => rows.flatMap((r) => (r as OrderRow).uncoded_pros || []),
    detailKind: 'uncoded',
    summarize: (rows) => rows.reduce((s, r) => s + ((r as OrderRow).uncoded_planned || 0), 0),
    format: (v) => (v == null ? '0' : String(Math.round(v))),
  },
  {
    key: 'otd_pct',
    label: 'On Time Delivery',
    description: '(Scheduled deliveries − controllable "Delivery Failure" service failures) ÷ scheduled deliveries. Only controllable SFs typed Delivery Failure count as late; Prepull, Empty Return, Cutoff and SOP failures (and untyped rows) still count as Service Failures but not against OTD. The rate can go negative when Delivery Failures exceed scheduled deliveries; a day with Delivery Failures but no scheduled deliveries shows -100%. A Delivery Failure counts against OTD even when the pro had no delivery scheduled that day.',
    summaryLabel: 'Avg',
    colorMode: 'threshold',
    sortBy: (rows) => sumDenom(rows) - sumOnTime(rows),
    linkPros: latePros,
    linkProsDedupe: false,
    detailKind: 'otd',
    cellTooltip: lateReasonTooltip,
    summarize: (rows) => {
      const num = rows.reduce((s, r) => s + ((r as OrderRow).otd_numerator || 0), 0);
      const den = rows.reduce((s, r) => s + ((r as OrderRow).otd_denominator || 0), 0);
      // Numerator can go negative (controllable SFs beyond scheduled deliveries);
      // show the negative rate. No scheduled deliveries but SFs -> -100%; nothing -> blank.
      if (den > 0) return (100 * num) / den;
      return num < 0 ? -100 : null;
    },
    hasData: (rows) =>
      rows.reduce((s, r) => s + ((r as OrderRow).otd_denominator || 0), 0) > 0 ||
      rows.reduce((s, r) => s + ((r as OrderRow).otd_numerator || 0), 0) < 0,
    detail: (rows) => {
      const num = rows.reduce((s, r) => s + ((r as OrderRow).otd_numerator || 0), 0);
      const den = rows.reduce((s, r) => s + ((r as OrderRow).otd_denominator || 0), 0);
      return den > 0 || num !== 0 ? `${num}/${den}` : null;
    },
    format: (v) => (v == null ? '—' : `${Math.round(v)}%`),
    extraCols: [
      {
        key: 'delivered',
        header: (<><div>Delivered</div><div style={{ fontSize: 10, opacity: 0.7 }}>On-Time / Total</div></>),
        minWidth: 110,
        cell: (rows) => `${fmtInt(sumOnTime(rows))} / ${fmtInt(sumDenom(rows))}`,
        footer: (rows) => `${fmtInt(sumOnTime(rows))} / ${fmtInt(sumDenom(rows))}`,
        sortValue: (rows) => sumDenom(rows),
      },
      {
        key: 'late',
        header: (<><div>Late</div><div style={{ fontSize: 10, opacity: 0.7 }}>Deliveries</div></>),
        minWidth: 54,
        cell: (rows, ctx) => renderLateCount(rows, ctx),
        footer: (rows, ctx) => renderLateCount(rows, ctx),
        sortValue: (rows) => sumDenom(rows) - sumOnTime(rows),
      },
      {
        key: 'otd_drag',
        header: (<><div>Impact (−)</div><div style={{ fontSize: 10, opacity: 0.7 }}>Late ÷ Total</div></>),
        minWidth: 118,
        cell: (rows, ctx) => {
          const pts = ctx.companyTotal > 0
            ? ((sumDenom(rows) - sumOnTime(rows)) / ctx.companyTotal) * 100 : 0;
          return renderDragCell(pts, ctx);
        },
        footer: (rows, ctx) => {
          const pts = ctx.companyTotal > 0
            ? ((sumDenom(rows) - sumOnTime(rows)) / ctx.companyTotal) * 100 : 0;
          return <span style={{ fontWeight: 700 }}>{pts.toFixed(2)}</span>;
        },
        sortValue: (rows) => sumDenom(rows) - sumOnTime(rows),
      },
      {
        key: 'otd_mvp',
        header: (<><div>Impact (+)</div><div style={{ fontSize: 10, opacity: 0.7 }}>Loads vs Avg</div></>),
        minWidth: 122,
        cell: (rows, ctx) => {
          const avg = ctx.companyTotal > 0 ? ctx.companyOnTime / ctx.companyTotal : 0;
          return renderMvpCell(sumOnTime(rows) - avg * sumDenom(rows), ctx);
        },
        sortValue: (rows, avg) => sumOnTime(rows) - avg * sumDenom(rows),
      },
    ],
    footerPanel: otdFooterPanel('dispatcher'),
  },
];

// Hovering a customer name lists the distinct dispatchers behind that customer's
// deliveries in the visible range (populated on the customer OTD cells).
const customerDispatchersTooltip = (rows: AnyRow[]): string | null => {
  const names = new Set<string>();
  for (const r of rows) for (const n of (r as OrderRow).dispatchers || []) names.add(n);
  return names.size > 0 ? Array.from(names).sort((a, b) => a.localeCompare(b)).join(', ') : null;
};

// Order metrics re-keyed on the customer axis (customer name occupies the
// dispatcher slot). Distinct `key`s so each table's column-sort state is
// independent of its dispatcher-axis twin. Backed by the same customer_rows.
export const CUSTOMER_OTD_METRIC: MetricDef = {
  ...ORDER_METRICS.find((m) => m.key === 'otd_pct')!,
  key: 'customer_otd',
  label: 'OTD by Customer',
  description: "Same OTD formula (on-time ÷ total deliveries; only controllable Delivery Failures count as late), grouped by the order's customer.",
  nameTooltip: customerDispatchersTooltip,
  // Same Company OTD / Total Late / Impact cards as the dispatcher table — this
  // table lives on its own tab, so it carries its own copy, worded for customers.
  footerPanel: otdFooterPanel('customer'),
};

export const CUSTOMER_SERVICE_FAILURES_METRIC: MetricDef = {
  ...ORDER_METRICS.find((m) => m.key === 'service_failures')!,
  key: 'customer_service_failures',
  label: 'Service Failures',
  description: "Distinct pros with a controllable (C:) service failure, grouped by the order's customer.",
  nameTooltip: customerDispatchersTooltip,
};

export const CUSTOMER_UNCODED_METRIC: MetricDef = {
  ...ORDER_METRICS.find((m) => m.key === 'uncoded_planned')!,
  key: 'customer_uncoded',
  label: 'Uncoded Planned Deliveries',
  description: "Planned deliveries that fell off the date with no coded service failure reason, grouped by the order's customer.",
  nameTooltip: customerDispatchersTooltip,
};

export const CUSTOMER_PASS_METRIC: MetricDef = {
  ...ORDER_METRICS.find((m) => m.key === 'pass_pct')!,
  key: 'customer_pass',
  label: 'Service %',
  description: "Delivered pros with no controllable (C:) service failure ÷ pros delivered, grouped by the order's customer. Any failure type, any date; a pro counts once.",
  nameTooltip: customerDispatchersTooltip,
};

// Order metrics re-keyed on the LC axis (the order's csr / Load Coordinator).
// Distinct `key`s keep column-sort state independent; backed by lc_rows. No
// name-hover — the LC *is* the axis, so there's nothing extra to break down.
export const LC_SERVICE_FAILURES_METRIC: MetricDef = {
  ...ORDER_METRICS.find((m) => m.key === 'service_failures')!,
  key: 'lc_service_failures',
  label: 'Service Failures',
  description: "Distinct pros with a controllable (C:) service failure, grouped by the order's LC.",
  nameTooltip: undefined,
};

export const LC_UNCODED_METRIC: MetricDef = {
  ...ORDER_METRICS.find((m) => m.key === 'uncoded_planned')!,
  key: 'lc_uncoded',
  label: 'Uncoded Planned Deliveries',
  description: "Planned deliveries that fell off the date with no coded service failure reason, grouped by the order's LC.",
  nameTooltip: undefined,
};

// Same Company OTD / Total Late / Impact cards, worded for LCs.
export const LC_OTD_METRIC: MetricDef = {
  ...ORDER_METRICS.find((m) => m.key === 'otd_pct')!,
  key: 'lc_otd',
  label: 'OTD by LC',
  description: "Same OTD formula (on-time ÷ total deliveries; only controllable Delivery Failures count as late), grouped by the order's LC.",
  nameTooltip: undefined,
  footerPanel: otdFooterPanel('LC'),
};

export const LC_PASS_METRIC: MetricDef = {
  ...ORDER_METRICS.find((m) => m.key === 'pass_pct')!,
  key: 'lc_pass',
  label: 'Service %',
  description: "Delivered pros with no controllable (C:) service failure ÷ pros delivered, grouped by the order's LC. Any failure type, any date; a pro counts once.",
  nameTooltip: undefined,
};

// Hovering a customer name on the LC page lists the distinct LCs behind that
// customer's deliveries in the visible range (the `lcs` breakdown the backend
// populates on the customer cells — distinct from the dispatcher-page hover).
const customerLcsTooltip = (rows: AnyRow[]): string | null => {
  const names = new Set<string>();
  for (const r of rows) for (const n of (r as OrderRow).lcs || []) names.add(n);
  return names.size > 0 ? Array.from(names).sort((a, b) => a.localeCompare(b)).join(', ') : null;
};

// Customer-axis configs for the LC Metrics page: same tables as CUSTOMER_*, but
// the name-hover lists LCs instead of dispatchers. Distinct keys keep sort state
// independent from the dispatcher page's customer tables.
export const LC_CUSTOMER_SERVICE_FAILURES_METRIC: MetricDef = {
  ...CUSTOMER_SERVICE_FAILURES_METRIC,
  key: 'lc_customer_service_failures',
  nameTooltip: customerLcsTooltip,
};

export const LC_CUSTOMER_UNCODED_METRIC: MetricDef = {
  ...CUSTOMER_UNCODED_METRIC,
  key: 'lc_customer_uncoded',
  nameTooltip: customerLcsTooltip,
};

export const LC_CUSTOMER_OTD_METRIC: MetricDef = {
  ...CUSTOMER_OTD_METRIC,
  key: 'lc_customer_otd',
  nameTooltip: customerLcsTooltip,
};

export const LC_CUSTOMER_PASS_METRIC: MetricDef = {
  ...CUSTOMER_PASS_METRIC,
  key: 'lc_customer_pass',
  nameTooltip: customerLcsTooltip,
};

// Human-friendly duration from a raw seconds value (Avg Time on LC).
export const fmtDuration = (seconds: number): string => {
  if (!isFinite(seconds) || seconds < 0) return '—';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const mins = seconds / 60;
  if (mins < 60) return `${Math.round(mins)}m`;
  const hours = mins / 60;
  if (hours < 24) return `${hours.toFixed(1)}h`;
  return `${(hours / 24).toFixed(1)}d`;
};

// ---- Scheduling metrics (LC 'Scheduling' group) ----------------------------
// Per-day throughput backed by schedule_rows. Pending LC / Hold / Pending
// Customer are current-state, so they render in a separate snapshot panel (not
// a MetricDef — see SchedulingSnapshot).
export const SCHED_DELIVERIES_METRIC: MetricDef = {
  key: 'lc_sched_deliveries',
  label: 'Appts Finalized',
  description: 'Delivery appointments the LC finalized (status → Finalized), counted on the finalize date. Click a count to see the pros.',
  summaryLabel: 'Total',
  colorMode: 'relative',
  emptyValue: 0,
  // Click a count to open the finalized-pros modal; linkPros is the fallback
  // (search link) when the detail modal isn't wired.
  linkPros: (rows) => rows.flatMap((r) => (r as ScheduleRow).delivery_pros || []),
  detailKind: 'apptsFinalized',
  summarize: (rows) => rows.reduce((s, r) => s + ((r as ScheduleRow).deliveries || 0), 0),
  format: (v) => (v == null ? '—' : String(Math.round(v))),
};

export const SCHED_AVG_TIME_ON_LC_METRIC: MetricDef = {
  key: 'lc_sched_avg_time',
  label: 'Avg Time on LC',
  description: 'Average time an order spent Pending LC before its delivery appointment was finalized (send-to-LC → finalize), by finalize date. Latest window per order; holds/returns excluded.',
  summaryLabel: 'Avg',
  colorMode: 'relative',
  sortAsc: true,  // less time on the LC is better
  hasData: (rows) => rows.reduce((s, r) => s + ((r as ScheduleRow).lc_time_n || 0), 0) > 0,
  summarize: (rows) => {
    const sum = rows.reduce((s, r) => s + ((r as ScheduleRow).lc_time_sum || 0), 0);
    const n = rows.reduce((s, r) => s + ((r as ScheduleRow).lc_time_n || 0), 0);
    return n > 0 ? sum / n : null;  // seconds
  },
  detail: (rows) => {
    const n = rows.reduce((s, r) => s + ((r as ScheduleRow).lc_time_n || 0), 0);
    return n > 0 ? `n=${n}` : null;
  },
  format: (v) => (v == null ? '—' : fmtDuration(v)),
};

// ---- View model ------------------------------------------------------------

// One table column: a single date (daily view) or a Monday-start week (weekly).
export interface Bucket {
  key: string;      // date, or the week's first visible date
  label: string;    // '6/22' or '6/22–6/26'
  sub: string;      // 'Mon' or 'Week'
  dates: string[];
}

export interface ViewModel {
  buckets: Bucket[];
  dispatchers: { key: string; name: string }[];
  byCell: Map<string, AnyRow[]>;    // `${dispatcher_key}|${bucket.key}`
  byDispatcher: Map<string, AnyRow[]>;
  byBucket: Map<string, AnyRow[]>;
  allVisible: AnyRow[];
}

// Build the matrix view model for a row set: visible date buckets (weekend
// filter, daily/weekly grouping), the dispatcher list, and lookups.
export function buildView(rows: AnyRow[], allDates: string[], excludeWeekends: boolean, weekly: boolean): ViewModel {
  const dates = allDates.filter((d) => !excludeWeekends || !isWeekend(d));

  const bucketOf = new Map<string, string>(); // date -> bucket key
  let buckets: Bucket[];
  if (weekly) {
    const byWeek = new Map<string, string[]>();
    for (const d of dates) {
      const k = weekStartKey(d);
      (byWeek.get(k) ?? byWeek.set(k, []).get(k)!).push(d);
    }
    buckets = Array.from(byWeek.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([, ds]) => {
        const key = ds[0]; // first visible date, so partial weeks label correctly
        for (const d of ds) bucketOf.set(d, key);
        return {
          key,
          dates: ds,
          label: ds.length > 1 ? `${fmtMD(ds[0])}–${fmtMD(ds[ds.length - 1])}` : fmtMD(ds[0]),
          sub: 'Week',
        };
      });
  } else {
    buckets = dates.map((d) => {
      bucketOf.set(d, d);
      return { key: d, dates: [d], label: fmtMD(d), sub: fmtDow(d) };
    });
  }

  const seen = new Map<string, string>();
  for (const r of rows) if (!seen.has(r.dispatcher_key)) seen.set(r.dispatcher_key, r.dispatcher);
  const dispatchers = Array.from(seen.entries())
    .map(([key, name]) => ({ key, name }))
    .sort((a, b) => a.name.localeCompare(b.name));

  const byCell = new Map<string, AnyRow[]>();
  const byDispatcher = new Map<string, AnyRow[]>();
  const byBucket = new Map<string, AnyRow[]>();
  const allVisible: AnyRow[] = [];
  for (const r of rows) {
    const bk = bucketOf.get(r.date);
    if (bk == null) continue;
    allVisible.push(r);
    const cellKey = `${r.dispatcher_key}|${bk}`;
    (byCell.get(cellKey) ?? byCell.set(cellKey, []).get(cellKey)!).push(r);
    (byDispatcher.get(r.dispatcher_key) ?? byDispatcher.set(r.dispatcher_key, []).get(r.dispatcher_key)!).push(r);
    (byBucket.get(bk) ?? byBucket.set(bk, []).get(bk)!).push(r);
  }
  return { buckets, dispatchers, byCell, byDispatcher, byBucket, allVisible };
}

// A signature of the current column set (granularity + count + first/last
// bucket key). Used to scope each table's settings and to force a remount when
// the date columns change — so columns always render in default chronological
// order instead of TanTable re-appending shifted dates at the end.
export const bucketSignature = (v: ViewModel): string => {
  const b = v.buckets;
  const gran = b[0]?.sub === 'Week' ? 'w' : 'd';
  return `${gran}-${b.length}-${b[0]?.key ?? ''}-${b[b.length - 1]?.key ?? ''}`;
};

// ---- Driver turnover -------------------------------------------------------

const QUARTER_MONTHS: Record<number, string> = {
  1: 'Jan-Mar', 2: 'Apr-Jun', 3: 'Jul-Sep', 4: 'Oct-Dec',
};

/** 'Q3 2026', or 'Total' for the roll-up across the whole window. */
export const periodLabel = (quarter: number, year: number): string =>
  quarter === 0 ? 'Total' : `Q${quarter} ${year}`;

/** 'Jul–Sep', blank for the roll-up. */
export const periodMonths = (quarter: number): string => QUARTER_MONTHS[quarter] ?? '';

/**
 * Separations over the average headcount for the period, as a percentage.
 *
 * The HR convention. Averaging the opening and closing headcount keeps a
 * dispatcher who grew and one who shrank on the same footing — but it collapses
 * toward zero when a period ends with no drivers, which is why the table marks
 * those cells rather than presenting them as comparable.
 */
export const turnoverPct = (row: TurnoverRow): number | null => {
  const avg = (row.start_count + row.finish_count) / 2;
  return avg > 0 ? (100 * row.terms) / avg : null;
};

/** 'YYYY-MM-DD' -> 'M/D/YY' (local, no UTC shift). */
export const fmtShortDate = (s?: string): string => {
  if (!s) return '';
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return s;
  return `${m}/${d}/${String(y).slice(2)}`;
};

/** '1/1/26 - 3/31/26', the span a period actually measured. */
export const periodRange = (start?: string, end?: string): string => {
  const a = fmtShortDate(start);
  const b = fmtShortDate(end);
  return a && b ? `${a} - ${b}` : a || b;
};

export interface TurnoverPeriod {
  quarter: number;
  year: number;
  label: string;
  start: string;
  end: string;
}

/** The periods present in a row set, oldest first with the roll-up last. */
export const turnoverPeriods = (rows: TurnoverRow[]): TurnoverPeriod[] => {
  const seen = new Map<string, TurnoverPeriod>();
  for (const r of rows) {
    const k = `${r.period_year}|${r.quarter}`;
    if (!seen.has(k)) {
      seen.set(k, {
        quarter: r.quarter,
        year: r.period_year,
        label: periodLabel(r.quarter, r.period_year),
        start: r.period_start,
        end: r.period_end,
      });
    }
  }
  return Array.from(seen.values()).sort((a, b) =>
    (a.quarter === 0 ? 1 : 0) - (b.quarter === 0 ? 1 : 0)
    || a.year - b.year
    || a.quarter - b.quarter);
};

/**
 * The Turnover tab's entry in the Driver metric group.
 *
 * Only `label` and `description` are read — turnover renders through
 * TurnoverTable, not the dispatcher x date matrix, because its four figures are
 * different measures rather than one measure over time. The remaining fields
 * satisfy MetricDef and are never called.
 */
export const TURNOVER_METRIC: MetricDef = {
  key: 'turnover',
  label: 'Turnover',
  description:
    'Driver headcount at the start and end of a quarter, the hires and terminations in '
    + 'between, and separations over the average headcount. A driver counts as hired on the '
    + 'day they join the roster, not the day they were hired on paper.',
  summaryLabel: 'Total',
  colorMode: 'none',
  summarize: () => null,
  format: (v) => (v == null ? '—' : String(v)),
};
